import { PLAN_IDS, type QuotaType } from '@/config/plans'
import type { Permissions } from '@/rbac/grants'
import { PERMISSIONS } from '@/rbac/permissions'

export type PlanId = (typeof PLAN_IDS)[keyof typeof PLAN_IDS]

/**
 * Machine-readable feature -> minimum plan. Single source of truth for plan gating.
 * Derived from PLAN_FEATURES in plans.ts.
 */
export const FEATURE_PLAN_MAP = {
	// Basic and up
	device_management: PLAN_IDS.BASIC,
	remote_commands: PLAN_IDS.BASIC,
	custom_commands: PLAN_IDS.BASIC,
	ota_campaigns: PLAN_IDS.BASIC,
	dashboards: PLAN_IDS.BASIC,
	dbc_parsers: PLAN_IDS.BASIC,
	rbac: PLAN_IDS.BASIC,
	remote_shell: PLAN_IDS.BASIC,

	// Pro and up
	alerts_full: PLAN_IDS.PRO,
	notifications: PLAN_IDS.PRO,
	payment_insights: PLAN_IDS.PRO,
	pushers: PLAN_IDS.PRO,

	// Enterprise only
	branding: PLAN_IDS.ENTERPRISE,
	pull_based_actions: PLAN_IDS.ENTERPRISE,
	root_project: PLAN_IDS.ENTERPRISE,
	dev_mode: PLAN_IDS.ENTERPRISE,
	custom_domain: PLAN_IDS.ENTERPRISE,
} as const satisfies Record<string, PlanId>

export type FeatureKey = keyof typeof FEATURE_PLAN_MAP

/**
 * The action a user is attempting against a resource.
 *
 * Each action reads exactly one slot off the resource config (no fallback between
 * slots): a slot left unset means that action carries no class-level RBAC requirement.
 *
 * - `view`: read-only access (list, detail). Uses viewPermission.
 * - `create`: create new instance. Uses createPermission.
 * - `edit`: mutate existing instance. Uses editPermission.
 * - `delete`: destroy existing instance. Uses deletePermission.
 * - `bulk`:  multi-entity mutation. Uses editPermission.
 * - `download`: pull the underlying artifact. Uses downloadPermission.
 */
export type AccessAction = 'view' | 'create' | 'edit' | 'delete' | 'bulk' | 'download'

export interface ResourceAccessConfig {
	id: string
	quotaKey?: QuotaType
	/** Permission required to create new instances. */
	createPermission?: keyof Permissions
	/** Permission required to view/list. Unset → no class-level view gate. */
	viewPermission?: keyof Permissions
	/** Permission required to edit existing instances. Unset → no class-level edit gate. */
	editPermission?: keyof Permissions
	/** Permission required to delete instances. Unset → no class-level delete gate. */
	deletePermission?: keyof Permissions
	/** Permission required to download the underlying artifact. Unset → no class-level download gate. */
	downloadPermission?: keyof Permissions
	/**
	 * Permission value evaluated for fine-grained checks when callers pass `entity` /
	 * `entities` into `useAccessControl`. Should match the grant shape (filter vs key/value).
	 *
	 * See `COMPLEX_KEY_ENTITY_KIND` for the usual `EntityDescriptor['kind']` pairing.
	 */
	complexPermissionKey?: keyof Permissions
	featureKey?: FeatureKey
	warningThreshold?: number
	billingHref: (orgId: string) => string
}

const billing = (orgId: string) => `/orgs/${orgId}/billing/plan-details`

export const ACCESS_RESOURCES = {
	projects: { id: 'projects', quotaKey: 'Projects', billingHref: billing },
	devices: {
		id: 'devices',
		quotaKey: 'Devices',
		viewPermission: PERMISSIONS.DEVICE_READ,
		createPermission: PERMISSIONS.DEVICE_PROVISION,
		editPermission: PERMISSIONS.DEVICE_UPDATE,
		deletePermission: PERMISSIONS.DEVICE_UPDATE,
		complexPermissionKey: PERMISSIONS.DEVICE_READ,
		billingHref: billing,
	},
	dashboards: {
		id: 'dashboards',
		quotaKey: 'Dashboards',
		viewPermission: PERMISSIONS.DASHBOARD_READ,
		createPermission: PERMISSIONS.DASHBOARD_CREATE,
		editPermission: PERMISSIONS.DASHBOARD_UPDATE,
		deletePermission: PERMISSIONS.DASHBOARD_DELETE,
		complexPermissionKey: PERMISSIONS.DASHBOARD_READ,
		billingHref: billing,
	},
	remote_commands: {
		id: 'remote_commands',
		quotaKey: 'Actions per Device',
		featureKey: 'remote_commands',
		viewPermission: PERMISSIONS.ACTION_TYPE_READ,
		complexPermissionKey: PERMISSIONS.ACTION_TYPE_EXECUTE,
		billingHref: billing,
	},
	custom_commands: {
		id: 'custom_commands',
		quotaKey: 'Action Types',
		featureKey: 'custom_commands',
		viewPermission: PERMISSIONS.ACTION_TYPE_READ,
		createPermission: PERMISSIONS.ACTION_TYPE_CREATE,
		editPermission: PERMISSIONS.ACTION_TYPE_UPDATE,
		deletePermission: PERMISSIONS.ACTION_TYPE_DELETE,
		complexPermissionKey: PERMISSIONS.ACTION_TYPE_READ,
		billingHref: billing,
	},
	streams: {
		id: 'streams',
		quotaKey: 'Streams',
		viewPermission: PERMISSIONS.STREAM_READ,
		createPermission: PERMISSIONS.STREAM_CREATE,
		editPermission: PERMISSIONS.STREAM_UPDATE,
		deletePermission: PERMISSIONS.STREAM_DELETE,
		complexPermissionKey: PERMISSIONS.STREAM_READ,
		billingHref: billing,
	},
	metadata_keys: {
		id: 'metadata_keys',
		quotaKey: 'Metadata Keys',
		viewPermission: PERMISSIONS.METADATA_READ,
		createPermission: PERMISSIONS.METADATA_CREATE,
		editPermission: PERMISSIONS.METADATA_UPDATE,
		deletePermission: PERMISSIONS.METADATA_DELETE,
		complexPermissionKey: PERMISSIONS.METADATA_READ,
		billingHref: billing,
	},
	users: {
		id: 'users',
		quotaKey: 'Users',
		viewPermission: PERMISSIONS.USER_READ,
		createPermission: PERMISSIONS.USER_CREATE,
		editPermission: PERMISSIONS.USER_UPDATE,
		deletePermission: PERMISSIONS.USER_DELETE,
		billingHref: billing,
	},
	roles: {
		id: 'roles',
		quotaKey: 'Roles',
		viewPermission: PERMISSIONS.ROLE_READ,
		createPermission: PERMISSIONS.ROLE_CREATE,
		editPermission: PERMISSIONS.ROLE_UPDATE,
		deletePermission: PERMISSIONS.ROLE_DELETE,
		billingHref: billing,
	},
	api_keys: {
		id: 'api_keys',
		quotaKey: 'API Keys',
		viewPermission: PERMISSIONS.API_KEY_READ,
		createPermission: PERMISSIONS.API_KEY_CREATE,
		editPermission: PERMISSIONS.API_KEY_UPDATE,
		deletePermission: PERMISSIONS.API_KEY_DELETE,
		billingHref: billing,
	},
	dbc_parsers: {
		id: 'dbc_parsers',
		quotaKey: 'DBC Parsers',
		viewPermission: PERMISSIONS.DBC_PARSER_READ,
		createPermission: PERMISSIONS.DBC_PARSER_CREATE,
		editPermission: PERMISSIONS.DBC_PARSER_UPDATE,
		deletePermission: PERMISSIONS.DBC_PARSER_DELETE,
		downloadPermission: PERMISSIONS.DBC_PARSER_FILE_DOWNLOAD,
		complexPermissionKey: PERMISSIONS.DBC_PARSER_READ,
		featureKey: 'dbc_parsers',
		billingHref: billing,
	},
	exporters: {
		id: 'exporters',
		quotaKey: 'Exporters',
		viewPermission: PERMISSIONS.EXPORTER_READ,
		createPermission: PERMISSIONS.EXPORTER_CREATE,
		editPermission: PERMISSIONS.EXPORTER_UPDATE,
		deletePermission: PERMISSIONS.EXPORTER_DELETE,
		complexPermissionKey: PERMISSIONS.EXPORTER_READ,
		billingHref: billing,
	},
	subscriber_groups: {
		id: 'subscriber_groups',
		quotaKey: 'Subscriber Groups',
		createPermission: PERMISSIONS.SUBSCRIBER_GROUP_CREATE,
		viewPermission: PERMISSIONS.SUBSCRIBER_GROUP_READ,
		editPermission: PERMISSIONS.SUBSCRIBER_GROUP_UPDATE,
		deletePermission: PERMISSIONS.SUBSCRIBER_GROUP_DELETE,
		complexPermissionKey: PERMISSIONS.SUBSCRIBER_GROUP_READ,
		featureKey: 'notifications',
		billingHref: billing,
	},
	session_rules: {
		id: 'session_rules',
		quotaKey: 'Session Rules',
		createPermission: PERMISSIONS.SESSION_RULE_CREATE,
		viewPermission: PERMISSIONS.SESSION_RULE_READ,
		editPermission: PERMISSIONS.SESSION_RULE_UPDATE,
		deletePermission: PERMISSIONS.SESSION_RULE_DELETE,
		complexPermissionKey: PERMISSIONS.SESSION_RULE_READ,
		billingHref: billing,
	},

	/** OTA / firmware artifacts — list-scoped via `firmware:read`. */
	firmware: {
		id: 'firmware',
		viewPermission: PERMISSIONS.FIRMWARE_READ,
		createPermission: PERMISSIONS.FIRMWARE_CREATE,
		editPermission: PERMISSIONS.FIRMWARE_UPDATE,
		deletePermission: PERMISSIONS.FIRMWARE_DELETE,
		downloadPermission: PERMISSIONS.FIRMWARE_DOWNLOAD,
		complexPermissionKey: PERMISSIONS.FIRMWARE_READ,
		featureKey: 'ota_campaigns',
		billingHref: billing,
	},
	/** Project file assets — list-scoped via `files:read`. */
	files: {
		id: 'files',
		viewPermission: PERMISSIONS.FILES_READ,
		createPermission: PERMISSIONS.FILES_CREATE,
		editPermission: PERMISSIONS.FILES_UPDATE,
		deletePermission: PERMISSIONS.FILES_DELETE,
		downloadPermission: PERMISSIONS.FILES_DOWNLOAD,
		complexPermissionKey: PERMISSIONS.FILES_READ,
		billingHref: billing,
	},
	/** JSON configuration assets — governed by the common list-scoped `config:*` permission. */
	json_config: {
		id: 'json_config',
		viewPermission: PERMISSIONS.CONFIG_READ,
		createPermission: PERMISSIONS.CONFIG_CREATE,
		editPermission: PERMISSIONS.CONFIG_UPDATE,
		deletePermission: PERMISSIONS.CONFIG_DELETE,
		billingHref: billing,
	},
	/** Geofence configuration assets — governed by the common list-scoped `config:*` permission. */
	geofence_config: {
		id: 'geofence_config',
		viewPermission: PERMISSIONS.CONFIG_READ,
		createPermission: PERMISSIONS.CONFIG_CREATE,
		editPermission: PERMISSIONS.CONFIG_UPDATE,
		deletePermission: PERMISSIONS.CONFIG_DELETE,
		billingHref: billing,
	},
	/** Notification rules (email/slack/webhook, etc.) — boolean scope. */
	notifications: {
		id: 'notifications',
		viewPermission: PERMISSIONS.NOTIFICATION_READ,
		createPermission: PERMISSIONS.NOTIFICATION_CREATE,
		editPermission: PERMISSIONS.NOTIFICATION_UPDATE,
		deletePermission: PERMISSIONS.NOTIFICATION_DELETE,
		billingHref: billing,
	},
	/** Tenant project settings (general, quotas entry, etc.). */
	project_settings: {
		id: 'project_settings',
		viewPermission: PERMISSIONS.PROJECT_SETTINGS_READ,
		editPermission: PERMISSIONS.PROJECT_SETTINGS_UPDATE,
		billingHref: billing,
	},

	// Plan-only features (no quota, no create-permission — purely plan-gated)
	branding: { id: 'branding', featureKey: 'branding', billingHref: billing },
	ota_campaigns: {
		id: 'ota_campaigns',
		featureKey: 'ota_campaigns',
		viewPermission: PERMISSIONS.FIRMWARE_READ,
		createPermission: PERMISSIONS.FIRMWARE_CREATE,
		editPermission: PERMISSIONS.FIRMWARE_UPDATE,
		deletePermission: PERMISSIONS.FIRMWARE_DELETE,
		downloadPermission: PERMISSIONS.FIRMWARE_DOWNLOAD,
		complexPermissionKey: PERMISSIONS.FIRMWARE_READ,
		billingHref: billing,
	},
	pull_based_actions: {
		id: 'pull_based_actions',
		featureKey: 'pull_based_actions',
		billingHref: billing,
	},
	dev_mode: { id: 'dev_mode', featureKey: 'dev_mode', billingHref: billing },
	pushers: { id: 'pushers', featureKey: 'pushers', billingHref: billing },
} as const satisfies Record<string, ResourceAccessConfig>

export type ResourceId = keyof typeof ACCESS_RESOURCES

/**
 * Discriminator labels for which `EntityDescriptor` shape typically accompanies a
 * `complexPermissionKey`. Stream field checks use `kind: 'stream'` with `field` set.
 */
export type ComplexEntityKind =
	| 'device'
	| 'stream'
	| 'action_type'
	| 'metadata'
	| 'metadata_key'
	| 'firmware'
	| 'files'
	| 'dbc_parser'
	| 'session_rule'
	| 'exporter'

/**
 * Resolve the Permissions key for a given resource + action:
 *   - view     -> viewPermission
 *   - create   -> createPermission
 *   - edit     -> editPermission
 *   - delete   -> deletePermission
 *   - bulk     -> editPermission
 *   - download -> downloadPermission
 *
 * Returns undefined when the action-specific permission is not set —
 * the caller should treat that as "no class-level RBAC required".
 */
export function pickPermissionKey(
	cfg: ResourceAccessConfig | undefined,
	action: AccessAction,
): keyof Permissions | undefined {
	if (!cfg) {
		return undefined
	}
	switch (action) {
		case 'view':
			return cfg.viewPermission
		case 'create':
			return cfg.createPermission
		case 'edit':
			return cfg.editPermission
		case 'delete':
			return cfg.deletePermission
		case 'bulk':
			return cfg.editPermission
		case 'download':
			return cfg.downloadPermission
	}
}
